import * as Comlink from "comlink";
import { getEnvironmentVariables } from "project-templates/environmentVariables";
import { templateLoader } from "project-templates/templateLoader";
import { runtimeEnv } from "configuration";
import { RootState } from "store/reduxStore";
import { joinUrl } from "utils/helpers";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { SourceFile } from "@sandbox/code-editor";
import { BuildResult, compileSass, FileSet, getSassWorker, transpiler, TranspilerOptions } from "@sandbox/transpiler";
import { sandboxFilesSelector } from "../active-sandbox/sandboxSelectors";
import { processStatusChanged, transpilerStatusChanged } from "./transpilerSlice";
import { getFilePath } from "../active-sandbox/sandboxSlice";

export const initTranspiler = createAsyncThunk(
  "transpiler/initialize",
  async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    if (state.transpiler.esbuildInitialized)
      return;

    thunkApi.dispatch(transpilerStatusChanged("initializing"));
    getSassWorker(joinUrl(runtimeEnv.REACT_APP_SANDBOXURL, runtimeEnv.REACT_APP_BASEURL, "sass.worker.js"), true);
    return transpiler.initialize();
  }
);

export const transpile = createAsyncThunk(
  "transpiler/transpile",
  async (_, thunkApi) => {
    thunkApi.dispatch(transpilerStatusChanged("building"));
    thunkApi.dispatch(processStatusChanged("building"));

    const state = thunkApi.getState() as RootState;
    const files = sandboxFilesSelector(state);

    if (files.some((file) => getFilePath(file).match(/\.spec\.(ts|tsx)$/)))
      thunkApi.dispatch(transpileTests());

    const entryFilePath = getEntry(files);
    if (!entryFilePath) {
      return {
        errors: [{
          text: [
            "Entry file is not set in File Explorer. Right click on the file containing the main app component and select \"Set Entry\".",
            "The file with main app component must have a default export.",
            "\n",
            "For example: ",
            "\n",
            "const App = () => {",
            "   return <h1>Hello world!</h1>;",
            "}",
            "export default App;"
          ].join("\n")
        }]
      };
    }

    if (!state.activeSandbox.resolvedPackages) {
      return {
        errors: [{
          text: "Could not resolve packages."
        }]
      };
    }

    const templateName = state.activeSandbox.resolvedPackages.find((pkg) => pkg.name === "@itwin/sandbox") ? "react-legacy" : "react";

    const sourceFiles = getFileSet(files);
    sourceFiles["/sandbox-index.tsx"] = await templateLoader(templateName, entryFilePath);

    const options: TranspilerOptions = {
      environmentVariables: getEnvironmentVariables(state.settings.theme, state.activeSandbox.selectedImodel),
      sourceFiles,
      entryFilePath: "/sandbox-index.tsx",
      imports: state.activeSandbox.resolvedPackages,
      minify: false,
      sourcemap: true,
    }

    const result: Promise<BuildResult> = transpiler.transpile(options, Comlink.proxy(compileSass));
    return result;
  }
);

export const transpileTests = createAsyncThunk(
  "transpiler/transpileTests",
  async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const files = sandboxFilesSelector(state);
    const sourceFiles = getFileSet(files);

    const tests = files.filter((file) => getFilePath(file).match(/\.spec\.(ts|tsx)$/));
    const results: Promise<BuildResult>[] = [];

    for (const test of tests) {
      const options: TranspilerOptions = {
        environmentVariables: getEnvironmentVariables(state.settings.theme, state.activeSandbox.selectedImodel),
        sourceFiles,
        entryFilePath: getFilePath(test),
        imports: state.activeSandbox.resolvedPackages,
        minify: false,
        sourcemap: false,
      }

      const result: Promise<BuildResult> = transpiler.transpile(options, Comlink.proxy(compileSass));
      results.push(result);
    }

    const allResults: Promise<BuildResult[]> = Promise.all(results);
    return allResults;
  }
);

// Helpers
function getEntry(files: SourceFile[]) {
  return files.find((item) => item.entry)?.filePath;
}

function getFileSet(files: SourceFile[]) {
  return files
    .filter((item) => item.type === "file")
    .reduce<FileSet>((prev, curr) => {
      prev[curr.filePath] = curr.content ?? ""
      return prev;
    }, {});
}